import { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProject, listVersions, listFilesInVersion } from '../lib/data';
import { createVersion, deleteVersion, deleteProject } from '../lib/api';
import type { Project, Version, FileItem } from '../lib/types';
import { Uploader } from '../components/Uploader';
import { FileList } from '../components/FileList';
import { NameDialog } from '../components/NameDialog';
import { Button, Spinner, EmptyState } from '../components/ui';

function VersionBlock({ version, onChanged }: { version: Version; onChanged: () => void }) {
  const [files, setFiles] = useState<FileItem[] | null>(null);
  const reload = useCallback(async () => setFiles(await listFilesInVersion(version.id)), [version.id]);
  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium">{version.label}</h3>
          {version.notes && <p className="text-sm text-slate-500">{version.notes}</p>}
        </div>
        <Button
          variant="ghost"
          onClick={async () => {
            if (window.confirm(`Delete version “${version.label}” and its files?`)) {
              await deleteVersion(version.id);
              onChanged();
            }
          }}
          aria-label="Delete version"
        >
          🗑
        </Button>
      </div>
      <div className="mt-3">
        <Uploader target={{ kind: 'version', id: version.id }} onUploaded={reload} />
      </div>
      <div className="mt-3">{files === null ? <Spinner /> : <FileList files={files} onChanged={reload} />}</div>
    </div>
  );
}

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null | undefined>(undefined);
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [dialog, setDialog] = useState(false);

  const reloadVersions = useCallback(async () => {
    if (id) setVersions(await listVersions(id));
  }, [id]);

  useEffect(() => {
    if (id) getProject(id).then(setProject).catch(() => setProject(null));
  }, [id]);
  useEffect(() => {
    reloadVersions();
  }, [reloadVersions]);

  if (project === undefined) return <Spinner label="Loading…" />;
  if (!project) return <p className="text-slate-500">Project not found.</p>;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-700">
            My Files
          </Link>
          <span>/</span>
          <span className="font-medium text-slate-800">{project.name}</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setDialog(true)}>＋ Add version</Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (window.confirm('Delete this project and all its versions?')) {
                await deleteProject(project.id);
                navigate('/');
              }
            }}
          >
            Delete project
          </Button>
        </div>
      </div>
      {project.description && <p className="mb-4 text-slate-600">{project.description}</p>}

      {versions === null ? (
        <Spinner />
      ) : versions.length === 0 ? (
        <EmptyState
          title="No versions yet"
          hint="Add a version, then upload files into it."
          action={<Button onClick={() => setDialog(true)}>Add version</Button>}
        />
      ) : (
        <div className="space-y-4">
          {versions.map((v) => (
            <VersionBlock key={v.id} version={v} onChanged={reloadVersions} />
          ))}
        </div>
      )}

      {dialog && (
        <NameDialog
          title="New version"
          label="Version label (e.g. v1, Client review)"
          extraField={{ label: 'Notes (optional)' }}
          onClose={() => setDialog(false)}
          onSubmit={async (label, notes) => {
            await createVersion(project.id, label, notes || undefined);
            await reloadVersions();
          }}
        />
      )}
    </div>
  );
}
